import { useMemo } from "react";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { getSFDCData } from "@/entities/Deal/Deal.queries";
import { longFormMoney, formatDate } from "@/utils/misc/format";
import { DetailTableRow } from "../components/DetailTable";
import {
    USE_OF_FUNDS,
    UseOfFunds,
} from "@/entities/CapitalApplication/CapitalApplication.constants";
import {
    CapitalProductTypeLabels,
    FinanceLabField,
    Frequency,
    FrequencyLabels,
    getFinanceLabMetrics,
    ResampleTransform,
} from "@/entities/FinanceLab";
import { DataSource, useColorizedValue } from "./useColorizedValue";
import { COMING_SOON } from "@/entities/CreditBox/CreditBox.types";
import { useMediaQuery } from "@/ui/hooks/useMediaQuery";
import {
    mobileRightDrawerOpenAtom,
    rightDrawerOpenAtom,
} from "@/features/Navigation/state";
import { useAtom, useSetAtom } from "jotai";
import { selectedMetricForDrawerAtom } from "../state";
import { useFlags } from "@/utils/flagsmith/useFlags";
import { getLastValue } from "@/utils/metrics/metricsUtils";

interface UseDealStructureDataProps {
    opportunityId?: string;
}

export function useDealStructureData({
    opportunityId,
}: UseDealStructureDataProps = {}) {
    const flags = useFlags();
    const showComingSoon = flags.enabled("show-coming-soon-placeholders");
    const showPreAlphaFields = flags.enabled("show-pre-alpha-fields");
    const isMobile = useMediaQuery("(max-width: 640px)");
    const [, setMobileRightDrawerOpen] = useAtom(mobileRightDrawerOpenAtom);
    const [, setRightDrawerOpen] = useAtom(rightDrawerOpenAtom);
    const setSelectedMetricForDrawer = useSetAtom(selectedMetricForDrawerAtom);

    const sfdcData = useApiQuery(getSFDCData);
    const data = opportunityId
        ? sfdcData.data?.find((data) => data.id === opportunityId)
        : sfdcData.data?.[0];

    const currentYearMonth = useMemo(() => new Date().toISOString(), []);
    const previousYearMonth = useMemo(() => {
        const date = new Date();
        date.setFullYear(date.getFullYear() - 1);
        return date.toISOString();
    }, []);

    const { data: metrics, isLoading: isLoadingMetrics } = useApiQuery(
        getFinanceLabMetrics,
        {
            params: {
                labels: [
                    FinanceLabField.DEAL_CAPITAL_PRODUCT,
                    FinanceLabField.DEAL_INITIAL_DISBURSEMENT,
                    FinanceLabField.DEAL_TERM,
                    FinanceLabField.DEAL_CURRENT_PAY_P,
                    FinanceLabField.DEAL_CURRENT_PAY_CASH_P,
                    FinanceLabField.DEAL_CURRENT_PAY_TYPE,
                    FinanceLabField.DEAL_BROKER_FEE,
                    FinanceLabField.DEAL_MOIC,
                    FinanceLabField.DEAL_IRR,
                    FinanceLabField.DEAL_MONITORING_FEE,
                    FinanceLabField.DEAL_PREPAYMENT_PENALTY,
                    FinanceLabField.DEAL_LEGAL_FEES,
                    FinanceLabField.DEAL_RECURRING_PAYMENT,
                    FinanceLabField.DEAL_REPAYMENT_FREQUENCY,
                    FinanceLabField.DEAL_SECURITY_PACKAGE,
                    FinanceLabField.DEAL_FUNDED_DATED,
                    FinanceLabField.DEAL_USE_OF_PROCEEDS,
                    FinanceLabField.DEAL_OPERATIONAL_CONTROLS,
                    FinanceLabField.DEAL_CLOSE_DATE,
                    FinanceLabField.DEAL_COVENANTS_SUMMARY,
                ],
                from: previousYearMonth,
                to: currentYearMonth,
            },
        },
    );

    const { data: mfiMetrics, isLoading: isLoadingMfiMetrics } = useApiQuery(
        getFinanceLabMetrics,
        {
            params: {
                labels: [
                    FinanceLabField.DEAL_COLLECTIONS,
                    FinanceLabField.DEAL_DISBURSEMENTS,
                ],
                resampleTransform: ResampleTransform.MONTHLY_FROM_INCEPTION,
            },
        },
    );

    const handleMetricClick = (
        financeLabField: FinanceLabField,
        title: string,
    ) => {
        const metric = [...(metrics ?? []), ...(mfiMetrics ?? [])]?.find(
            (m) => m.info.derived.label === financeLabField,
        );

        setSelectedMetricForDrawer({
            metric,
            title,
        });

        if (isMobile) {
            setMobileRightDrawerOpen(true);
        } else {
            setRightDrawerOpen(true);
        }
    };

    const capitalProductValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_CAPITAL_PRODUCT,
    )?.values;
    const initialDisbursementValues = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_INITIAL_DISBURSEMENT,
    )?.values;
    const termValues = metrics?.find(
        (metric) => metric.info.derived.label === FinanceLabField.DEAL_TERM,
    )?.values;
    const currentPayPValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_CURRENT_PAY_P,
    )?.values;
    const repaymentFrequencyValues = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_REPAYMENT_FREQUENCY,
    )?.values;
    const currentPayTypeValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_CURRENT_PAY_TYPE,
    )?.values;

    const brokerFeeValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_BROKER_FEE,
    )?.values;

    const moicValues = metrics?.find(
        (metric) => metric.info.derived.label === FinanceLabField.DEAL_MOIC,
    )?.values;

    const irrValues = metrics?.find(
        (metric) => metric.info.derived.label === FinanceLabField.DEAL_IRR,
    )?.values;

    const monitoringFeeValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_MONITORING_FEE,
    )?.values;

    const prepaymentPenaltyValues = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_PREPAYMENT_PENALTY,
    )?.values;

    const legalFeesValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_LEGAL_FEES,
    )?.values;

    const recurringPaymentValues = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_RECURRING_PAYMENT,
    )?.values;

    const fundedDatedValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_FUNDED_DATED,
    )?.values;

    const collectionsValues = mfiMetrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_COLLECTIONS,
    )?.values;

    const disbursementsValues = mfiMetrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_DISBURSEMENTS,
    )?.values;

    const closeDateValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_CLOSE_DATE,
    )?.values;

    const cashYieldValues = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_CURRENT_PAY_CASH_P,
    )?.values;

    const covenantsSummaryValues = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_COVENANTS_SUMMARY,
    )?.values;

    const capitalProductRaw = getLastValue(capitalProductValues);
    const capitalProductLabel = capitalProductRaw?.value
        ? CapitalProductTypeLabels[capitalProductRaw.value]
        : undefined;
    const capitalTypeColorized = useColorizedValue(
        capitalProductLabel ?? capitalProductRaw?.formattedValue ?? "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_CAPITAL_PRODUCT,
    );
    const facilitySizeColorized = useColorizedValue(
        longFormMoney(data?.totalFacilitySize),
        DataSource.SFDC,
        "sfdc.totalFacilitySize",
    );
    const initialAdvanceColorized = useColorizedValue(
        getLastValue(initialDisbursementValues)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_INITIAL_DISBURSEMENT,
    );
    const termValue = getLastValue(termValues);
    const facilityTermColorized = useColorizedValue(
        termValue?.value ? `${termValue.value} months` : undefined,
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_TERM,
    );

    const productData: DetailTableRow[] = [
        {
            label: "Capital Type",
            value: capitalTypeColorized.value,
            valueClassName: capitalTypeColorized.className,
            source: capitalTypeColorized.source,
            sourceClassName: capitalTypeColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_CAPITAL_PRODUCT,
                    "Capital Type",
                ),
        },
        {
            label: "Initial Advance",
            value: initialAdvanceColorized.value,
            valueClassName: initialAdvanceColorized.className,
            source: initialAdvanceColorized.source,
            sourceClassName: initialAdvanceColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_INITIAL_DISBURSEMENT,
                    "Initial Advance",
                ),
        },
        {
            label: "Facility Size",
            value: facilitySizeColorized.value,
            valueClassName: facilitySizeColorized.className,
            source: facilitySizeColorized.source,
            sourceClassName: facilitySizeColorized.sourceClassName,
        },
        ...(showPreAlphaFields
            ? [
                  {
                      label: "Facility Term",
                      value: facilityTermColorized.value,
                      valueClassName: facilityTermColorized.className,
                      source: facilityTermColorized.source,
                      sourceClassName: facilityTermColorized.sourceClassName,
                      onClick: () =>
                          handleMetricClick(
                              FinanceLabField.DEAL_TERM,
                              "Facility Term",
                          ),
                  },
              ]
            : []),
    ];

    const interestRateColorized = useColorizedValue(
        getLastValue(currentPayPValues)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_CURRENT_PAY_P,
    );
    const originationFeeColorized = useColorizedValue(
        longFormMoney(data?.originationFees),
        DataSource.SFDC,
        "sfdc.originationFees",
    );
    const moicColorized = useColorizedValue(
        getLastValue(moicValues)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_MOIC,
    );
    const cashYieldColorized = useColorizedValue(
        getLastValue(cashYieldValues)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_CURRENT_PAY_CASH_P,
    );
    const legalFeesColorized = useColorizedValue(
        getLastValue(legalFeesValues)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_LEGAL_FEES,
    );
    const feeTypeColorized = useColorizedValue(
        getLastValue(currentPayTypeValues)?.formattedValue ||
            getLastValue(currentPayTypeValues)?.value ||
            "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_CURRENT_PAY_TYPE,
    );
    const brokerFeeColorized = useColorizedValue(
        getLastValue(brokerFeeValues)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_BROKER_FEE,
    );
    const irrColorized = useColorizedValue(
        getLastValue(irrValues)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_IRR,
    );
    const monitoringFeeColorized = useColorizedValue(
        getLastValue(monitoringFeeValues)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_MONITORING_FEE,
    );
    const prepaymentPenaltyColorized = useColorizedValue(
        getLastValue(prepaymentPenaltyValues)?.formattedValue ||
            getLastValue(prepaymentPenaltyValues)?.value ||
            "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_PREPAYMENT_PENALTY,
    );

    const recurringPaymentRaw = getLastValue(recurringPaymentValues);
    const recurringPaymentValue =
        recurringPaymentRaw?.formattedValue ||
        recurringPaymentRaw?.value ||
        "-";
    // Extract only the value before parentheses for display
    const recurringPaymentDisplay =
        typeof recurringPaymentValue === "string" &&
        recurringPaymentValue.includes("(")
            ? recurringPaymentValue.split("(")[0].trim()
            : recurringPaymentValue;
    const monthlyPaymentColorized = useColorizedValue(
        recurringPaymentDisplay,
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_RECURRING_PAYMENT,
    );
    const repaymentFrequencyRaw = getLastValue(repaymentFrequencyValues);
    const repaymentFrequencyLabel =
        repaymentFrequencyRaw?.value &&
        typeof repaymentFrequencyRaw.value === "string"
            ? FrequencyLabels[repaymentFrequencyRaw.value as Frequency]
            : undefined;
    const repaymentFrequencyColorized = useColorizedValue(
        repaymentFrequencyLabel ??
            repaymentFrequencyRaw?.formattedValue ??
            repaymentFrequencyRaw?.value ??
            "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_REPAYMENT_FREQUENCY,
    );
    const targetCloseDateColorized = useColorizedValue(
        data?.closeDate
            ? formatDate(data.closeDate, {
                  hideTime: true,
                  parseAsUTC: false,
                  dateFormat: "YYYY-MM-DD",
              })
            : "-",
        DataSource.SFDC,
        "sfdc.closeDate",
    );
    const actualFundingDateColorized = useColorizedValue(
        getLastValue(fundedDatedValues)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_FUNDED_DATED,
    );
    const collectionsColorized = useColorizedValue(
        getLastValue(collectionsValues)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_COLLECTIONS,
    );
    const actualCloseDateColorized = useColorizedValue(
        getLastValue(closeDateValues)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_CLOSE_DATE,
    );
    const advancesColorized = useColorizedValue(
        getLastValue(disbursementsValues)?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_DISBURSEMENTS,
    );
    const useOfProceedsColorized = useColorizedValue(
        USE_OF_FUNDS[data?.useOfProceeds as UseOfFunds],
        DataSource.SFDC,
        "sfdc.useOfProceeds",
    );

    const covenantsColorized = useColorizedValue(
        getLastValue(covenantsSummaryValues)?.formattedValue ||
            getLastValue(covenantsSummaryValues)?.value ||
            "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.DEAL_COVENANTS_SUMMARY,
    );

    const economicsData: DetailTableRow[] = [
        ...(showPreAlphaFields
            ? [
                  {
                      label: "Fee Type",
                      value: feeTypeColorized.value,
                      valueClassName: feeTypeColorized.className,
                      source: feeTypeColorized.source,
                      sourceClassName: feeTypeColorized.sourceClassName,
                      onClick: () =>
                          handleMetricClick(
                              FinanceLabField.DEAL_CURRENT_PAY_TYPE,
                              "Fee Type",
                          ),
                  },
              ]
            : []),
        {
            label: "Referral Fee",
            value: brokerFeeColorized.value,
            valueClassName: brokerFeeColorized.className,
            source: brokerFeeColorized.source,
            sourceClassName: brokerFeeColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_BROKER_FEE,
                    "Referral Fee",
                ),
        },
        ...(showPreAlphaFields
            ? [
                  {
                      label: "Interest Rate/Fee",
                      value: interestRateColorized.value,
                      valueClassName: interestRateColorized.className,
                      source: interestRateColorized.source,
                      sourceClassName: interestRateColorized.sourceClassName,
                      onClick: () =>
                          handleMetricClick(
                              FinanceLabField.DEAL_CURRENT_PAY_P,
                              "Interest Rate/Fee",
                          ),
                  },
                  {
                      label: "Prepayment Penalty",
                      value: prepaymentPenaltyColorized.value,
                      valueClassName: prepaymentPenaltyColorized.className,
                      source: prepaymentPenaltyColorized.source,
                      sourceClassName:
                          prepaymentPenaltyColorized.sourceClassName,
                      onClick: () =>
                          handleMetricClick(
                              FinanceLabField.DEAL_PREPAYMENT_PENALTY,
                              "Prepayment Penalty",
                          ),
                  },
              ]
            : []),
        {
            label: "Origination Fee",
            value: originationFeeColorized.value,
            valueClassName: originationFeeColorized.className,
            source: originationFeeColorized.source,
            sourceClassName: originationFeeColorized.sourceClassName,
        },
        {
            label: "Cash Yield",
            value: cashYieldColorized.value,
            valueClassName: cashYieldColorized.className,
            source: cashYieldColorized.source,
            sourceClassName: cashYieldColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_CURRENT_PAY_CASH_P,
                    "Cash Yield",
                ),
        },
        ...(showPreAlphaFields
            ? [
                  {
                      label: "Monitoring Fee",
                      value: monitoringFeeColorized.value,
                      valueClassName: monitoringFeeColorized.className,
                      source: monitoringFeeColorized.source,
                      sourceClassName: monitoringFeeColorized.sourceClassName,
                      onClick: () =>
                          handleMetricClick(
                              FinanceLabField.DEAL_MONITORING_FEE,
                              "Monitoring Fee",
                          ),
                  },
                  {
                      label: "MOIC",
                      value: moicColorized.value,
                      valueClassName: moicColorized.className,
                      source: moicColorized.source,
                      sourceClassName: moicColorized.sourceClassName,
                      onClick: () =>
                          handleMetricClick(FinanceLabField.DEAL_MOIC, "MOIC"),
                  },
                  {
                      label: "Legal Fees",
                      value: legalFeesColorized.value,
                      valueClassName: legalFeesColorized.className,
                      source: legalFeesColorized.source,
                      sourceClassName: legalFeesColorized.sourceClassName,
                      onClick: () =>
                          handleMetricClick(
                              FinanceLabField.DEAL_LEGAL_FEES,
                              "Legal Fees",
                          ),
                  },
                  {
                      label: "IRR",
                      value: irrColorized.value,
                      valueClassName: irrColorized.className,
                      source: irrColorized.source,
                      sourceClassName: irrColorized.sourceClassName,
                      onClick: () =>
                          handleMetricClick(FinanceLabField.DEAL_IRR, "IRR"),
                  },
              ]
            : []),
    ];

    const termsConditionsData: DetailTableRow[] = [
        {
            label: "Target Close Date",
            value: targetCloseDateColorized.value,
            valueClassName: targetCloseDateColorized.className,
            source: targetCloseDateColorized.source,
            sourceClassName: targetCloseDateColorized.sourceClassName,
        },
        {
            label: "Repayment Frequency",
            value: repaymentFrequencyColorized.value,
            valueClassName: repaymentFrequencyColorized.className,
            source: repaymentFrequencyColorized.source,
            sourceClassName: repaymentFrequencyColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_REPAYMENT_FREQUENCY,
                    "Repayment Frequency",
                ),
        },
        {
            label: "Actual Close Date",
            value: actualCloseDateColorized.value,
            valueClassName: actualCloseDateColorized.className,
            source: actualCloseDateColorized.source,
            sourceClassName: actualCloseDateColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_CLOSE_DATE,
                    "Actual Close Date",
                ),
        },
        {
            label: "Total Advanced To Date",
            value: advancesColorized.value,
            valueClassName: advancesColorized.className,
            source: advancesColorized.source,
            sourceClassName: advancesColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_DISBURSEMENTS,
                    "Total Advanced To Date",
                ),
        },
        {
            label: "Actual Funding Date",
            value: actualFundingDateColorized.value,
            valueClassName: actualFundingDateColorized.className,
            source: actualFundingDateColorized.source,
            sourceClassName: actualFundingDateColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_FUNDED_DATED,
                    "Actual Funding Date",
                ),
        },
        ...(showComingSoon
            ? [
                  {
                      label: "Security",
                      value: COMING_SOON,
                  },
              ]
            : []),
        {
            label: "Collections To Date",
            value: collectionsColorized.value,
            valueClassName: collectionsColorized.className,
            source: collectionsColorized.source,
            sourceClassName: collectionsColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_COLLECTIONS,
                    "Collections To Date",
                ),
        },
        ...(showComingSoon
            ? [
                  {
                      label: "Security Features",
                      value: COMING_SOON,
                  },
              ]
            : []),
        {
            label: "Repayment Amount",
            value: monthlyPaymentColorized.value,
            valueClassName: monthlyPaymentColorized.className,
            source: monthlyPaymentColorized.source,
            sourceClassName: monthlyPaymentColorized.sourceClassName,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_RECURRING_PAYMENT,
                    "Repayment Amount",
                ),
        },
        {
            label: "Use of Proceeds",
            value: useOfProceedsColorized.value,
            valueClassName: useOfProceedsColorized.className,
            source: useOfProceedsColorized.source,
            sourceClassName: useOfProceedsColorized.sourceClassName,
        },
    ];

    return {
        productData,
        economicsData,
        termsConditionsData,
        covenantsSummaryValues,
        covenantsColorized,
        isLoading:
            sfdcData.isLoading || isLoadingMetrics || isLoadingMfiMetrics,
        error: sfdcData.error,
    };
}
